import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Flame, Trophy, X } from '@phosphor-icons/react';
import Confetti from './Confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MILESTONE_META = {
  7:   { label: 'Week One',    sub: '7 DAYS',   tone: '#C0392F' },
  30:  { label: 'Iron Month',  sub: '30 DAYS',  tone: '#1E5AA0' },
  100: { label: 'Century',     sub: '100 DAYS', tone: '#8A5A44' },
};

/**
 * Full-screen milestone celebration. Fires confetti, shows a badge, and pings
 * the backend to mark the milestone acknowledged so it never re-appears.
 */
export default function MilestoneModal({ weeks, onClose }) {
  const meta = MILESTONE_META[weeks];
  const [ack, setAck] = useState(false);

  useEffect(() => {
    if (ack) return;
    axios.post(
      `${API_URL}/progress/streak/ack-milestone`,
      { weeks },
      { withCredentials: true },
    ).then(() => setAck(true)).catch(() => setAck(true));
  }, [weeks, ack]);

  if (!meta) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-6 py-10 bg-graphite/85 backdrop-blur-lg animate-fade-in"
      data-testid="milestone-modal"
      onClick={onClose}
    >
      <Confetti />
      <div
        className="relative w-full max-w-sm ios-card p-8 text-center milestone-badge-hero"
        onClick={(e) => e.stopPropagation()}
        style={{ backgroundColor: 'var(--bone-warm)' }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-ios-pill hover:bg-graphite/5 flex items-center justify-center"
          data-testid="milestone-close-button"
          aria-label="Close"
        >
          <X size={18} weight="bold" />
        </button>

        <p className="text-[10px] uppercase tracking-[0.32em] font-bold text-graphite/50 mb-2">
          Streak Milestone
        </p>

        <div
          className="mx-auto w-36 h-36 rounded-full flex items-center justify-center mb-4 shadow-ios"
          style={{
            background: `radial-gradient(120% 120% at 30% 20%, ${meta.tone} 0%, ${meta.tone}CC 55%, ${meta.tone}80 100%)`,
            border: `4px solid ${meta.tone}`,
          }}
          data-testid={`milestone-badge-${weeks}`}
        >
          <div className="flex flex-col items-center">
            <p className="text-6xl font-black font-heading text-bone leading-none tabular-nums">
              {weeks}
            </p>
            <p className="text-[9px] uppercase tracking-[0.24em] font-black text-bone/85 mt-1">
              Weeks
            </p>
          </div>
        </div>

        <h2 className="text-3xl font-black font-heading uppercase text-graphite tracking-tight">
          {meta.label}
        </h2>
        <p className="text-[11px] uppercase tracking-[0.28em] font-bold text-userRed mt-1">
          {meta.sub}
        </p>
        <p className="text-sm text-graphite/70 mt-4 leading-relaxed">
          {weeks} days of training in a row. That's showing up when it counts. Keep the fire.
        </p>

        <button
          onClick={onClose}
          className="mt-6 w-full h-12 rounded-ios-pill bg-graphite text-bone font-black text-xs uppercase tracking-[0.18em]"
          data-testid="milestone-continue-button"
        >
          Keep Going
        </button>
      </div>
    </div>
  );
}

/** Compact badge chip used on the Streak widget's badge shelf. */
export function MilestoneBadge({ weeks, earned, testId }) {
  const meta = MILESTONE_META[weeks];
  if (!meta) return null;
  return (
    <div
      className={`flex-1 py-2 rounded-ios flex flex-col items-center text-center ${
        earned ? 'shadow-ios' : 'bg-graphite/5'
      }`}
      style={earned ? { backgroundColor: meta.tone, color: '#EDEAE3' } : {}}
      data-testid={testId}
      data-earned={earned}
    >
      <Trophy size={14} weight={earned ? 'fill' : 'regular'} className={earned ? 'text-bone' : 'text-graphite/40'} />
      <p className={`text-lg font-black font-heading tabular-nums leading-none mt-0.5 ${earned ? 'text-bone' : 'text-graphite/45'}`}>
        {weeks}
      </p>
      <p className={`text-[8px] uppercase tracking-[0.18em] font-black mt-0.5 ${earned ? 'text-bone/85' : 'text-graphite/40'}`}>
        {earned ? meta.label : 'Locked'}
      </p>
    </div>
  );
}
